// `broadcast-channel.service.ng1.ts` — the page's side of the fan-out bus.
//
// Two implementations behind one interface, and the choice is made PER SEND rather than at
// construction. Upstream's comment says why: "On time of creation we can't know if the new
// implementation can be used or not (content script loading takes time) ... we wrap both
// channels and send messages to one of them only when message need to be sent, this way, we
// postpone the decision to the maximum time possible." A channel created during the SPA's
// boot would otherwise be fixed to the fallback for its whole life.
import {
  extensionBroadcastAvailable,
  WooSiteMessageResponseType,
  type BroadcastEnvelope,
  type WooBroadcastGlobal,
} from './broadcast.ts'

export interface BroadcastMessage {
  type: string
  /** Stamped by `BroadcastChannelService`; present on everything that arrives. */
  uniqueId?: number
}

export interface BroadcastChannelManager {
  /**
   * Generic so that a caller may post one of the richer message shapes that extend
   * `BroadcastMessage` — `UserAuthStateChangeMessage` and the four others sharing the iframe
   * topic all add a `data` of their own. A plain `BroadcastMessage` parameter would reject
   * every one of them as an excess property, at the call site, for carrying its payload.
   */
  postMessage: <M extends BroadcastMessage>(message: M) => void
  destroy: () => void
}

/** Just enough of `window` to run under test without a DOM. */
export interface BroadcastHost {
  addEventListener: (type: string, listener: (event: MessageEvent) => void) => void
  removeEventListener: (type: string, listener: (event: MessageEvent) => void) => void
}

type Listener = (message: BroadcastMessage) => void

/**
 * The slice of `BroadcastChannel` this uses.
 *
 * The listener is typed `Event`, not `MessageEvent`, because that is what the DOM's own
 * `addEventListener` accepts — narrowing it here makes `new BroadcastChannel(topic)` fail to
 * satisfy the interface it is meant to describe.
 */
export interface NativeChannelLike {
  postMessage: (message: unknown) => void
  addEventListener: (type: string, listener: (event: Event) => void) => void
  removeEventListener: (type: string, listener: (event: Event) => void) => void
  close: () => void
}

export interface BroadcastChannelOptions {
  /** Defaults to `globalThis`; injected by tests and by the two non-window callers. */
  host?: BroadcastHost & WooBroadcastGlobal
  /** Defaults to `BroadcastChannel`; absent in Node, so tests pass a stub. */
  nativeChannel?: (topic: string) => NativeChannelLike
}

/**
 * One per page. The id it stamps is what makes `ignoreSelfMessages` possible.
 *
 * ⚠️ THE ID IS PER PAGE, NOT PER CHANNEL. Two channels in the same page must share it, or a
 * surface subscribing to two topics would hear its own message on the second one.
 */
export class BroadcastChannelService {
  // Upstream's `new Date().getTime()`, with its own `// TODO avif: Change this to Tab id`.
  // Kept: a timestamp collides only if two surfaces start in the same millisecond, and the
  // consequence of a collision is one ignored message rather than a wrong one.
  private readonly uniqueId = Date.now()
  private readonly host: BroadcastHost & WooBroadcastGlobal
  private readonly nativeChannel: NonNullable<BroadcastChannelOptions['nativeChannel']>

  constructor(options: BroadcastChannelOptions = {}) {
    this.host = options.host ?? (globalThis as unknown as BroadcastHost & WooBroadcastGlobal)
    this.nativeChannel =
      options.nativeChannel ?? ((topic: string) => new BroadcastChannel(topic))
  }

  getUniqueId(): number {
    return this.uniqueId
  }

  /** `shouldUseNewImplementation()`. Re-read on every send — see the note at the top. */
  usesExtensionRoute(): boolean {
    return extensionBroadcastAvailable(this.host)
  }

  createAndSubscribe(
    topic: string,
    onMessage: Listener,
    options: { ignoreSelfMessages?: boolean } = {},
  ): BroadcastChannelManager {
    const ignoreSelf = options.ignoreSelfMessages ?? true
    const deliver = (message: BroadcastMessage): void => {
      // ⚠️ THE FAN-OUT REACHES THE SENDER TOO. The worker posts to every matching tab, this
      // one included, so without the id check a sign-out raised here comes straight back.
      if (ignoreSelf && message.uniqueId === this.uniqueId) return
      onMessage(message)
    }

    const native = this.nativeChannel(topic)
    const onNative = (event: Event): void => {
      deliver((event as MessageEvent).data as BroadcastMessage)
    }
    native.addEventListener('message', onNative)

    const onWindowMessage = (event: MessageEvent): void => {
      const data = event.data as { type?: string; data?: BroadcastEnvelope } | null
      if (data === null || data.type !== WooSiteMessageResponseType.PostWooBroadcastMessage) return
      const envelope = data.data
      if (envelope === undefined || envelope.topic !== topic) return
      // Upstream drops a payload with no `type`, rather than passing it on: the listeners
      // all switch on it, and one without is a malformed send.
      const message = envelope.data as BroadcastMessage | undefined
      if (message === undefined || typeof message.type !== 'string') return
      deliver(message)
    }
    this.host.addEventListener('message', onWindowMessage)

    return {
      postMessage: <M extends BroadcastMessage>(message: M) => {
        const stamped = { ...message, uniqueId: this.uniqueId }
        if (this.usesExtensionRoute()) {
          this.host.wooBroadcastMessage?.({ topic, data: stamped })
          return
        }
        native.postMessage(stamped)
      },
      destroy: () => {
        native.removeEventListener('message', onNative)
        this.host.removeEventListener('message', onWindowMessage)
        native.close()
      },
    }
  }
}
